import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FileText, Building2, CalendarDays, ChevronRight } from "lucide-react"
import { format, isPast, isFuture } from "date-fns"
import { toast } from "sonner"

import { APP_NAME } from "@/lib/appConfig"
import { useLeases, useUnassignLease, useReactivateLease } from "@/features/leases/hooks/useLeases"
import { useCurrency } from "@/hooks/useCurrency"
import { PageHeader } from "@/components/shared/PageHeader"
import { EmptyState } from "@/components/shared/EmptyState"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import type { Lease } from "@rentbase/shared"

// ─── Types ────────────────────────────────────────────────────────────────────

type LeaseFull = Lease & {
  tenant?: { id?: string; full_name?: string; email?: string }
  property?: { id?: string; name?: string; city?: string }
}

interface PropertyGroup {
  propertyId: string
  propertyName: string
  propertyCity: string | null
  leases: LeaseFull[]
  activeCount: number
}

type PendingAction = { type: "terminate" | "reactivate"; lease: LeaseFull }

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveStatus(lease: LeaseFull): string {
  if (lease.status && lease.status !== "active") return lease.status
  if (lease.end_date && isPast(new Date(lease.end_date))) return "expired"
  if (lease.start_date && isFuture(new Date(lease.start_date))) return "upcoming"
  return "active"
}

function statusConfig(status: string) {
  switch (status) {
    case "active":
      return { dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Active" }
    case "upcoming":
      return { dot: "bg-blue-400", pill: "bg-blue-50 text-blue-700 border-blue-200", label: "Upcoming" }
    case "expired":
      return { dot: "bg-muted-foreground/40", pill: "bg-muted text-muted-foreground border-border", label: "Expired" }
    case "terminated":
      return { dot: "bg-destructive/70", pill: "bg-destructive/10 text-destructive border-destructive/20", label: "Terminated" }
    default:
      return { dot: "bg-muted-foreground/40", pill: "bg-muted text-muted-foreground border-border", label: status }
  }
}

function formatDateRange(start?: string, end?: string | null) {
  const s = start ? format(new Date(start), "d MMM yyyy") : "—"
  const e = end ? format(new Date(end), "d MMM yyyy") : "Ongoing"
  return { start: s, end: e }
}

function TenantAvatar({ name }: { name?: string }) {
  const initial = (name ?? "?")[0].toUpperCase()
  return (
    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
      <span className="text-sm font-bold text-primary">{initial}</span>
    </div>
  )
}

// ─── Lease Card ───────────────────────────────────────────────────────────────

function LeaseCard({
  lease,
  onAction,
}: {
  lease: LeaseFull
  onAction: (type: "terminate" | "reactivate", lease: LeaseFull) => void
}) {
  const navigate = useNavigate()
  const { format: formatCurrency } = useCurrency()
  const status = deriveStatus(lease)
  const cfg = statusConfig(status)
  const { start, end } = formatDateRange(lease.start_date, lease.end_date)

  return (
    <div
      className="rounded-2xl bg-card border border-border/60 hover:border-primary/20 hover:shadow-sm transition-all overflow-hidden cursor-pointer group"
      onClick={() => navigate(`/leases/${lease.id}`)}
    >
      {/* Card header row */}
      <div className="flex items-center gap-3 px-4 pt-3.5 pb-3 border-b border-border/50">
        <TenantAvatar name={lease.tenant?.full_name} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate leading-tight">
            {lease.tenant?.full_name ?? "Unknown tenant"}
          </p>
          {lease.tenant?.email && (
            <p className="text-[11px] text-muted-foreground truncate">{lease.tenant.email}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${cfg.pill}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
      </div>

      {/* Data columns row */}
      <div className="grid grid-cols-3 divide-x divide-border/50 px-0">
        <div className="flex flex-col px-4 py-2.5">
          <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">Start</span>
          <span className="text-xs font-semibold text-foreground">{start}</span>
        </div>
        <div className="flex flex-col px-4 py-2.5">
          <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">End</span>
          <span className="text-xs font-semibold text-foreground">{end}</span>
        </div>
        <div className="flex flex-col px-4 py-2.5">
          <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">Monthly</span>
          <span className="text-xs font-bold text-foreground">
            {lease.monthly_rent != null ? formatCurrency(lease.monthly_rent) : "—"}
          </span>
        </div>
      </div>

      {/* Action row */}
      {(status === "active" || status === "upcoming" || status === "terminated") && (
        <div className="px-4 py-2.5 border-t border-border/50 flex justify-end">
          {(status === "active" || status === "upcoming") && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAction("terminate", lease) }}
              className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-destructive/30 text-destructive/70 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/60 transition-colors"
            >
              Terminate
            </button>
          )}
          {status === "terminated" && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAction("reactivate", lease) }}
              className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-emerald-400/40 text-emerald-600/70 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-500 transition-colors"
            >
              Reactivate
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Property Group ───────────────────────────────────────────────────────────

function PropertyGroup({
  group,
  onLeaseAction,
}: {
  group: PropertyGroup
  onLeaseAction: (type: "terminate" | "reactivate", lease: LeaseFull) => void
}) {
  return (
    <div>
      {/* Property header */}
      <div className="flex items-center gap-2.5 mb-2.5 px-1">
        <div className="w-7 h-7 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Building2 className="h-3.5 w-3.5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate leading-tight">{group.propertyName}</p>
          {group.propertyCity && (
            <p className="text-[10px] text-muted-foreground truncate">{group.propertyCity}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {group.activeCount > 0 && (
            <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full">
              {group.activeCount} active
            </span>
          )}
          <span className="text-[10px] text-muted-foreground font-medium">
            {group.leases.length} lease{group.leases.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Lease cards */}
      <div className="space-y-2 pl-9">
        {group.leases.map((lease) => (
          <LeaseCard key={lease.id} lease={lease} onAction={onLeaseAction} />
        ))}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LeasesPage() {
  const { data: leases, isLoading, isError, refetch } = useLeases()
  const terminateLease = useUnassignLease()
  const reactivateLease = useReactivateLease()
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)

  useEffect(() => {
    const prev = document.title
    document.title = `Leases | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  // Group leases by property, sorted active-first within each group
  const groups = useMemo<PropertyGroup[]>(() => {
    if (!leases?.length) return []

    const map = new Map<string, PropertyGroup>()

    for (const lease of leases as LeaseFull[]) {
      const pid = lease.property?.id ?? "__unknown__"
      if (!map.has(pid)) {
        map.set(pid, {
          propertyId: pid,
          propertyName: lease.property?.name ?? "Unknown property",
          propertyCity: lease.property?.city ?? null,
          leases: [],
          activeCount: 0,
        })
      }
      const g = map.get(pid)!
      g.leases.push(lease)
      if (deriveStatus(lease) === "active") g.activeCount++
    }

    // Sort leases within each group: active first, then by start date desc
    for (const g of map.values()) {
      g.leases.sort((a, b) => {
        const aActive = deriveStatus(a) === "active" ? 0 : 1
        const bActive = deriveStatus(b) === "active" ? 0 : 1
        if (aActive !== bActive) return aActive - bActive
        return (b.start_date ?? "").localeCompare(a.start_date ?? "")
      })
    }

    // Sort groups: most active leases first
    return Array.from(map.values()).sort((a, b) => b.activeCount - a.activeCount)
  }, [leases])

  const totalActive = groups.reduce((s, g) => s + g.activeCount, 0)
  const totalLeases = groups.reduce((s, g) => s + g.leases.length, 0)

  function handleConfirmAction() {
    if (!pendingAction) return
    const { type, lease } = pendingAction

    if (type === "terminate") {
      terminateLease.mutate(lease.id, {
        onSuccess: () => {
          toast.success("Lease terminated and pending records removed.")
          setPendingAction(null)
        },
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          toast.error(msg ?? "Failed to terminate lease")
          setPendingAction(null)
        },
      })
    } else {
      reactivateLease.mutate(lease.id, {
        onSuccess: () => {
          toast.success("Lease reactivated and rent record created.")
          setPendingAction(null)
        },
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          toast.error(msg ?? "Failed to reactivate lease")
          setPendingAction(null)
        },
      })
    }
  }

  const isActionPending = terminateLease.isPending || reactivateLease.isPending

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Leases" />

      <div className="flex-1 overflow-y-auto pb-nav">
        {isLoading && <ListSkeleton count={4} />}

        {isError && (
          <div className="flex flex-col items-center py-16 px-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">Failed to load leases.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
          </div>
        )}

        {!isLoading && !isError && groups.length === 0 && (
          <EmptyState
            icon={<FileText className="h-9 w-9 text-muted-foreground" />}
            title="No leases yet"
            description="Leases linking tenants to properties will appear here."
          />
        )}

        {!isLoading && !isError && groups.length > 0 && (
          <>
            {/* Summary strip */}
            <div className="mx-4 mt-4 mb-5 flex divide-x divide-border/50 bg-card/60 backdrop-blur border border-border/50 rounded-2xl overflow-hidden">
              <div className="flex-1 py-3 px-4 flex flex-col items-center text-center">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Properties</span>
                <span className="text-xl font-extrabold">{groups.length}</span>
              </div>
              <div className="flex-1 py-3 px-4 flex flex-col items-center text-center">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Total</span>
                <span className="text-xl font-extrabold">{totalLeases}</span>
              </div>
              <div className="flex-1 py-3 px-4 flex flex-col items-center text-center">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Active</span>
                <span className="text-xl font-extrabold text-emerald-600">{totalActive}</span>
              </div>
            </div>

            {/* Grouped list */}
            <div className="px-4 space-y-6 pb-4">
              {groups.map((group) => (
                <PropertyGroup
                  key={group.propertyId}
                  group={group}
                  onLeaseAction={(type, lease) => setPendingAction({ type, lease })}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Terminate / Reactivate confirmation dialog */}
      <AlertDialog
        open={!!pendingAction}
        onOpenChange={(open) => { if (!open && !isActionPending) setPendingAction(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.type === "terminate" ? "Terminate lease?" : "Reactivate lease?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.type === "terminate" ? (
                <>
                  This will immediately terminate the lease for{" "}
                  <span className="font-medium text-foreground">
                    {pendingAction.lease.tenant?.full_name ?? "this tenant"}
                  </span>
                  {" "}and remove all pending (unpaid) rent records. Paid records will remain intact.
                </>
              ) : (
                <>
                  This will reactivate the lease for{" "}
                  <span className="font-medium text-foreground">
                    {pendingAction?.lease.tenant?.full_name ?? "this tenant"}
                  </span>
                  {" "}and generate a pending rent record for the current month.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActionPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              disabled={isActionPending}
              className={
                pendingAction?.type === "terminate"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }
            >
              {isActionPending
                ? "Processing…"
                : pendingAction?.type === "terminate"
                ? "Terminate"
                : "Reactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
